import React, { useState, useEffect } from 'react';
import { Card, Button, Collapse, Badge, ListGroup, Form, Modal } from 'react-bootstrap';
import { FaFileAlt, FaFileInvoiceDollar, FaExchangeAlt, FaReceipt, FaFolder, FaChevronDown, FaChevronRight, FaDownload, FaEnvelope, FaTrash, FaImage, FaUpload, FaLink, FaPaperPlane } from 'react-icons/fa';
import { downloadDocument } from '../../utils/documentUtils';
import { toast } from 'react-toastify';

const DocumentsTab = ({ 
  documents = [], 
  projectId,
  token, 
  onDeleteDocument, 
  onUploadDocument,
  onEmailDocument,
  clientEmail,
  clientName,
  projectName,
  uploadingFile,
  selectedFile,
  onFileChange 
}) => {
  const [openSections, setOpenSections] = useState({
    proposals: false,
    invoices: false,
    changeOrders: false,
    receipts: false,
    other: false
  });
  const [receipts, setReceipts] = useState([]);
  
  // Signing link modal state (for proposals and change orders)
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [signingLink, setSigningLink] = useState('');
  const [signingStatus, setSigningStatus] = useState('');
  const [signingDocType, setSigningDocType] = useState(''); // 'proposal' or 'change_order'
  const [signingChangeOrderId, setSigningChangeOrderId] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Invoice email modal state
  const [showInvoiceEmailModal, setShowInvoiceEmailModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceCustomMessage, setInvoiceCustomMessage] = useState('');
  const [isSendingInvoiceEmail, setIsSendingInvoiceEmail] = useState(false);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  // Receipt image viewer state
  const [showReceiptImageModal, setShowReceiptImageModal] = useState(false);
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isLoadingReceiptImage, setIsLoadingReceiptImage] = useState(false);
  const [receiptFileType, setReceiptFileType] = useState(null); // 'image' or 'pdf'

  // Fetch receipts for this project
  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const response = await fetch(`/api/receipts/project/${projectId}`, {
          headers: { 'x-auth-token': token }
        });
        if (response.ok) {
          const data = await response.json();
          setReceipts(data || []);
        }
      } catch (err) {
        // Receipts feature may not be enabled
        console.log('Receipts not available');
      }
    };
    
    if (projectId && token) {
      fetchReceipts();
    }
  }, [projectId, token]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Categorize documents
  const categorizeDocuments = () => {
    const categories = {
      proposals: [],
      invoices: [],
      changeOrders: [],
      other: []
    };

    documents.forEach(doc => {
      const name = doc.DocumentName?.toLowerCase() || '';
      const type = doc.DocumentType?.toLowerCase() || '';
      
      if (name.includes('proposal') || type.includes('proposal')) {
        categories.proposals.push(doc);
      } else if (name.includes('invoice') || type.includes('invoice')) {
        categories.invoices.push(doc);
      } else if (name.includes('change') || name.includes('order') || type.includes('change')) {
        categories.changeOrders.push(doc);
      } else {
        categories.other.push(doc);
      }
    });

    return categories;
  };

  const categories = categorizeDocuments();

  const getDocTypeIcon = (docName) => {
    const name = docName?.toLowerCase() || '';
    if (name.includes('proposal')) return <FaFileAlt className="text-primary" />;
    if (name.includes('invoice')) return <FaFileInvoiceDollar className="text-success" />;
    if (name.includes('change')) return <FaExchangeAlt className="text-warning" />;
    return <FaFolder className="text-secondary" />;
  };

  // Check if document is a proposal or change order (has signing link)
  const hasSigningLink = (docName) => {
    const name = docName?.toLowerCase() || '';
    return name.includes('proposal') || name.includes('change');
  };

  // Check if document is an invoice
  const isInvoiceDoc = (docName) => {
    const name = docName?.toLowerCase() || '';
    return name.includes('invoice');
  };

  // Determine document type from name
  const getDocType = (docName) => {
    const name = docName?.toLowerCase() || '';
    if (name.includes('change')) return 'change_order';
    if (name.includes('proposal')) return 'proposal';
    if (name.includes('invoice')) return 'invoice';
    return 'unknown';
  };

  // Fetch and show signing link
  const handleShowSigningLink = async (doc) => {
    try {
      const response = await fetch(`/api/documents/${doc.DocumentId}/signing-link`, {
        headers: { 'x-auth-token': token }
      });
      
      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${window.location.origin}/sign/${data.LinkToken}`;
        setSigningLink(fullUrl);
        setSigningStatus(data.Status);
        // Use DocumentType from API if available, otherwise infer from document name
        const docType = data.DocumentType || getDocType(doc.DocumentName);
        setSigningDocType(docType === 'change_order' ? 'change_order' : 'proposal');
        setSigningChangeOrderId(data.ChangeOrderId || null);
        setCustomMessage('');
        setShowSigningModal(true);
      } else {
        toast.info('No signing link available for this document');
      }
    } catch (err) {
      console.error('Error fetching signing link:', err);
      toast.error('Failed to get signing link');
    }
  };

  // Fetch and show invoice email modal
  const handleShowInvoiceEmailModal = async (doc) => {
    setIsLoadingInvoice(true);
    setShowInvoiceEmailModal(true);
    setInvoiceCustomMessage('');
    
    try {
      const response = await fetch(`/api/invoices/by-document/${doc.DocumentId}`, {
        headers: { 'x-auth-token': token }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvoiceData(data);
      } else {
        toast.error('Could not find invoice data for this document');
        setShowInvoiceEmailModal(false);
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      toast.error('Failed to load invoice data');
      setShowInvoiceEmailModal(false);
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  // Copy signing link to clipboard
  const copySigningLink = () => {
    navigator.clipboard.writeText(signingLink);
    toast.success('Signing link copied to clipboard!');
  };

  // Send signing link email (proposals and change orders)
  const handleSendSigningEmail = async () => {
    if (!clientEmail) {
      toast.error('Client does not have an email address');
      return;
    }

    setIsSendingEmail(true);
    
    try {
      let endpoint;
      if (signingDocType === 'change_order' && signingChangeOrderId) {
        endpoint = `/api/email/change-order/${signingChangeOrderId}/send`;
      } else {
        // Default to proposal
        endpoint = `/api/email/proposal/${projectId}/send`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({ customMessage: customMessage || null })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Email sent successfully to ${data.sentTo || clientEmail}`);
        setShowSigningModal(false);
        setCustomMessage('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to send email');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error('Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Send invoice email
  const handleSendInvoiceEmail = async () => {
    if (!invoiceData?.ClientEmail) {
      toast.error('Client does not have an email address');
      return;
    }

    setIsSendingInvoiceEmail(true);
    
    try {
      const response = await fetch(`/api/email/invoice/${invoiceData.InvoiceId}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({ customMessage: invoiceCustomMessage || null })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Invoice email sent successfully to ${data.sentTo || invoiceData.ClientEmail}`);
        setShowInvoiceEmailModal(false);
        setInvoiceCustomMessage('');
        setInvoiceData(null);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to send invoice email');
      }
    } catch (err) {
      console.error('Error sending invoice email:', err);
      toast.error('Failed to send invoice email');
    } finally {
      setIsSendingInvoiceEmail(false);
    }
  };

  // View receipt image
  const handleViewReceiptImage = async (receipt) => {
    setIsLoadingReceiptImage(true);
    setShowReceiptImageModal(true);
    setSelectedReceipt(receipt);
    setReceiptImageUrl(null);
    setReceiptFileType(null);
    
    // Determine file type from ImagePath
    const imagePath = receipt.ImagePath || '';
    const isPdf = imagePath.toLowerCase().endsWith('.pdf');
    setReceiptFileType(isPdf ? 'pdf' : 'image');
    
    try {
      const response = await fetch(`/api/receipts/${receipt.ScanId}/image`, {
        headers: { 'x-auth-token': token }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const fileUrl = URL.createObjectURL(blob);
        setReceiptImageUrl(fileUrl);
      } else {
        toast.error('Could not load receipt file');
        setShowReceiptImageModal(false);
      }
    } catch (err) {
      console.error('Error fetching receipt file:', err);
      toast.error('Failed to load receipt file');
      setShowReceiptImageModal(false);
    } finally {
      setIsLoadingReceiptImage(false);
    }
  };

  // Close receipt image modal and cleanup URL
  const handleCloseReceiptImageModal = () => {
    if (receiptImageUrl) {
      URL.revokeObjectURL(receiptImageUrl);
    }
    setShowReceiptImageModal(false);
    setReceiptImageUrl(null);
    setSelectedReceipt(null);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const renderDocumentItem = (doc) => {
    const isInvoice = isInvoiceDoc(doc.DocumentName);
    const hasSignLink = hasSigningLink(doc.DocumentName);
    
    return (
      <ListGroup.Item key={doc.DocumentId} className="d-flex justify-content-between align-items-center py-3">
        <div className="d-flex align-items-center">
          {getDocTypeIcon(doc.DocumentName)}
          <div className="ms-3">
            <div className="fw-medium">{doc.DocumentName}</div>
            <small className="text-muted">
              {new Date(doc.CreatedAt).toLocaleDateString()}
            </small>
          </div>
        </div>
        <div>
          {/* Signing link button for proposals and change orders */}
          {hasSignLink && (
            <Button
              variant="outline-info"
              size="sm"
              className="me-1"
              onClick={() => handleShowSigningLink(doc)}
              title="Get Signing Link & Send Email"
            >
              <FaLink />
            </Button>
          )}
          
          {/* Download button */}
          <Button
            variant="outline-primary"
            size="sm"
            className="me-1"
            onClick={() => {
              try {
                downloadDocument(doc.DocumentId, doc.DocumentName, token);
              } catch (err) {
                toast.error('Error downloading document');
              }
            }}
            title="Download"
          >
            <FaDownload />
          </Button>
          
          {/* Email button - different behavior for invoices vs other docs */}
          {isInvoice ? (
            <Button
              variant="outline-success"
              size="sm"
              className="me-1"
              disabled={!clientEmail}
              onClick={() => handleShowInvoiceEmailModal(doc)}
              title={!clientEmail ? "No client email" : "Send Invoice Email"}
            >
              <FaEnvelope />
            </Button>
          ) : !hasSignLink && (
            // For non-invoice, non-signing docs, use the old email handler
            <Button
              variant="outline-success"
              size="sm"
              className="me-1"
              disabled={!clientEmail}
              onClick={() => onEmailDocument && onEmailDocument(doc)}
              title={!clientEmail ? "No client email" : "Email to client"}
            >
              <FaEnvelope />
            </Button>
          )}
          
          {/* Delete button */}
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => onDeleteDocument && onDeleteDocument(doc.DocumentId)}
            title="Delete"
          >
            <FaTrash />
          </Button>
        </div>
      </ListGroup.Item>
    );
  };

  const renderReceiptItem = (receipt) => (
    <ListGroup.Item key={receipt.ScanId} className="d-flex justify-content-between align-items-center py-3">
      <div className="d-flex align-items-center">
        <FaReceipt className="text-info" />
        <div className="ms-3">
          <div className="fw-medium">
            {receipt.StoreName || 'Unknown Store'}
            <Badge 
              bg={receipt.Status === 'approved' ? 'success' : receipt.Status === 'pending' ? 'warning' : 'secondary'} 
              className="ms-2"
            >
              {receipt.Status}
            </Badge>
          </div>
          <small className="text-muted">
            {new Date(receipt.ScannedAt || receipt.CreatedAt).toLocaleDateString()} • 
            ${parseFloat(receipt.ReceiptTotal || receipt.TotalAmount || 0).toFixed(2)} • 
            {receipt.ItemCount || 0} items
          </small>
        </div>
      </div>
      <div>
        <Button
          variant="outline-info"
          size="sm"
          title="View Receipt Image"
          onClick={() => handleViewReceiptImage(receipt)}
        >
          <FaImage />
        </Button>
      </div>
    </ListGroup.Item>
  );

  const renderSection = (title, icon, items, sectionKey, renderItem, badgeColor = 'primary') => {
    // Don't render receipts section if no receipts
    if (sectionKey === 'receipts' && items.length === 0) {
      return null;
    }
    
    return (
      <Card className="mb-3" key={sectionKey}>
        <Card.Header 
          className="d-flex justify-content-between align-items-center py-2"
          style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="d-flex align-items-center">
            {openSections[sectionKey] ? <FaChevronDown className="me-2 text-muted" /> : <FaChevronRight className="me-2 text-muted" />}
            {icon}
            <span className="ms-2 fw-medium">{title}</span>
            <Badge bg={badgeColor} className="ms-2">{items.length}</Badge>
          </div>
        </Card.Header>
        <Collapse in={openSections[sectionKey]}>
          <div>
            {items.length > 0 ? (
              <ListGroup variant="flush">
                {items.map(renderItem)}
              </ListGroup>
            ) : (
              <Card.Body className="text-center text-muted py-4">
                No {title.toLowerCase()} yet
              </Card.Body>
            )}
          </div>
        </Collapse>
      </Card>
    );
  };

  return (
    <div>
      {/* Upload Section */}
      <Card className="mb-4">
        <Card.Header className="py-2" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="d-flex align-items-center">
            <FaUpload className="me-2 text-muted" />
            <span className="fw-medium">Upload Document</span>
          </div>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={onUploadDocument}>
            <div className="d-flex gap-2">
              <Form.Control
                type="file"
                id="document-upload"
                onChange={onFileChange}
                className="flex-grow-1"
              />
              <Button 
                type="submit" 
                variant="primary"
                disabled={!selectedFile || uploadingFile}
              >
                {uploadingFile ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Document Sections */}
      {renderSection('Proposals', <FaFileAlt className="text-primary" />, categories.proposals, 'proposals', renderDocumentItem, 'primary')}
      {renderSection('Change Orders', <FaExchangeAlt className="text-warning" />, categories.changeOrders, 'changeOrders', renderDocumentItem, 'warning')}
      {renderSection('Invoices', <FaFileInvoiceDollar className="text-success" />, categories.invoices, 'invoices', renderDocumentItem, 'success')}
      {renderSection('Receipts', <FaReceipt className="text-info" />, receipts, 'receipts', renderReceiptItem, 'info')}
      {renderSection('Other Documents', <FaFolder className="text-secondary" />, categories.other, 'other', renderDocumentItem, 'secondary')}

      {/* Empty state if no documents at all */}
      {documents.length === 0 && receipts.length === 0 && (
        <div className="text-center text-muted py-5">
          <FaFolder size={48} className="mb-3" />
          <p>No documents yet. Upload a document or generate a proposal to get started.</p>
        </div>
      )}

      {/* Signing Link Modal with Email Option (for Proposals and Change Orders) */}
      <Modal show={showSigningModal} onHide={() => setShowSigningModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaLink className="me-2" />
            Electronic Signature Link
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Status */}
          <div className="mb-3">
            <label className="form-label fw-medium">Status</label>
            <div>
              <Badge bg={signingStatus === 'signed' ? 'success' : signingStatus === 'pending' ? 'warning' : 'secondary'}>
                {signingStatus === 'signed' ? 'Signed' : signingStatus === 'pending' ? 'Awaiting Signature' : signingStatus}
              </Badge>
            </div>
          </div>

          {/* Signing Link */}
          <div className="mb-4">
            <label className="form-label fw-medium">Signing Link</label>
            <div className="input-group">
              <input 
                type="text" 
                className="form-control" 
                value={signingLink} 
                readOnly 
                onClick={(e) => e.target.select()}
              />
              <Button variant="outline-primary" onClick={copySigningLink}>
                Copy Link
              </Button>
            </div>
            <small className="text-muted">
              Copy this link to share manually via text or other methods.
            </small>
          </div>

          <hr />

          {/* Email Section */}
          <div className="mt-4">
            <h6 className="fw-bold mb-3">
              <FaEnvelope className="me-2" />
              Send Signing Link via Email
            </h6>
            
            {clientEmail ? (
              <>
                <div className="mb-3 p-3 bg-light rounded">
                  <div className="row">
                    <div className="col-sm-3 text-muted">To:</div>
                    <div className="col-sm-9">{clientName || 'Client'} ({clientEmail})</div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-sm-3 text-muted">Subject:</div>
                    <div className="col-sm-9">
                      {signingDocType === 'change_order' 
                        ? `Change Order for ${projectName || 'Your Project'}` 
                        : `Your Proposal is Ready - ${projectName || 'Your Project'}`
                      }
                    </div>
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Add a Personal Message (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Add a personal note to your client..."
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    This message will appear at the top of the email.
                  </Form.Text>
                </Form.Group>

                <Button 
                  variant={signingDocType === 'change_order' ? 'warning' : 'primary'}
                  onClick={handleSendSigningEmail}
                  disabled={isSendingEmail}
                  className="w-100"
                >
                  {isSendingEmail ? (
                    <>Sending...</>
                  ) : (
                    <>
                      <FaPaperPlane className="me-2" />
                      Send {signingDocType === 'change_order' ? 'Change Order' : 'Proposal'} Email
                    </>
                  )}
                </Button>
              </>
            ) : (
              <div className="alert alert-warning mb-0">
                <strong>No client email on file.</strong> Add an email address to the client record to send signing links via email.
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSigningModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invoice Email Modal */}
      <Modal show={showInvoiceEmailModal} onHide={() => { setShowInvoiceEmailModal(false); setInvoiceData(null); }} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: '#d4edda', borderBottom: '3px solid #28a745' }}>
          <Modal.Title>
            <FaFileInvoiceDollar className="me-2 text-success" />
            Send Invoice Email
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingInvoice ? (
            <div className="text-center py-4">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading invoice data...</p>
            </div>
          ) : invoiceData ? (
            <>
              {/* Invoice Summary */}
              <div className="mb-4 p-3 bg-light rounded border-start border-success border-4">
                <div className="row">
                  <div className="col-sm-6">
                    <p className="mb-1"><strong>Invoice #:</strong> {invoiceData.InvoiceNumber}</p>
                    <p className="mb-1"><strong>Project:</strong> {invoiceData.ProjectName}</p>
                    <p className="mb-0"><strong>Status:</strong> 
                      <Badge bg={invoiceData.Status === 'paid' ? 'success' : invoiceData.Status === 'sent' ? 'info' : 'secondary'} className="ms-2">
                        {invoiceData.Status}
                      </Badge>
                    </p>
                  </div>
                  <div className="col-sm-6 text-sm-end">
                    <p className="mb-1"><strong>Amount Due:</strong></p>
                    <h3 className="text-success mb-0">{formatCurrency(invoiceData.TotalDue)}</h3>
                    {invoiceData.DueDate && (
                      <small className="text-muted">Due: {new Date(invoiceData.DueDate).toLocaleDateString()}</small>
                    )}
                  </div>
                </div>
              </div>

              <hr />

              {/* Email Section */}
              <h6 className="fw-bold mb-3">
                <FaEnvelope className="me-2" />
                Email Details
              </h6>
              
              {invoiceData.ClientEmail ? (
                <>
                  <div className="mb-3 p-3 bg-light rounded">
                    <div className="row">
                      <div className="col-sm-3 text-muted">To:</div>
                      <div className="col-sm-9">{invoiceData.ClientName} ({invoiceData.ClientEmail})</div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-sm-3 text-muted">Subject:</div>
                      <div className="col-sm-9">Invoice {invoiceData.InvoiceNumber} - {invoiceData.ProjectName}</div>
                    </div>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Add a Personal Message (Optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Add a personal note to your client..."
                      value={invoiceCustomMessage}
                      onChange={(e) => setInvoiceCustomMessage(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      This message will appear at the top of the email.
                    </Form.Text>
                  </Form.Group>

                  <Button 
                    variant="success"
                    onClick={handleSendInvoiceEmail}
                    disabled={isSendingInvoiceEmail}
                    className="w-100"
                  >
                    {isSendingInvoiceEmail ? (
                      <>Sending...</>
                    ) : (
                      <>
                        <FaPaperPlane className="me-2" />
                        Send Invoice Email
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <div className="alert alert-warning mb-0">
                  <strong>No client email on file.</strong> Add an email address to the client record to send invoice emails.
                </div>
              )}
            </>
          ) : (
            <div className="alert alert-danger">
              Could not load invoice data. Please try again.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowInvoiceEmailModal(false); setInvoiceData(null); }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Receipt Image/PDF Modal */}
      <Modal show={showReceiptImageModal} onHide={handleCloseReceiptImageModal} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#d1ecf1', borderBottom: '3px solid #17a2b8' }}>
          <Modal.Title>
            <FaImage className="me-2 text-info" />
            Receipt {receiptFileType === 'pdf' ? 'Document' : 'Image'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-4">
          {isLoadingReceiptImage ? (
            <div className="py-5">
              <div className="spinner-border text-info" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading receipt {receiptFileType === 'pdf' ? 'document' : 'image'}...</p>
            </div>
          ) : receiptImageUrl ? (
            <>
              {/* Receipt Info */}
              {selectedReceipt && (
                <div className="mb-3 p-3 bg-light rounded text-start">
                  <div className="row">
                    <div className="col-sm-6">
                      <p className="mb-1"><strong>Store:</strong> {selectedReceipt.StoreName || 'Unknown'}</p>
                      <p className="mb-1"><strong>Date:</strong> {selectedReceipt.ReceiptDate ? new Date(selectedReceipt.ReceiptDate).toLocaleDateString() : 'N/A'}</p>
                    </div>
                    <div className="col-sm-6">
                      <p className="mb-1"><strong>Total:</strong> ${parseFloat(selectedReceipt.ReceiptTotal || 0).toFixed(2)}</p>
                      <p className="mb-1"><strong>Status:</strong> 
                        <Badge 
                          bg={selectedReceipt.Status === 'approved' ? 'success' : selectedReceipt.Status === 'pending' ? 'warning' : 'secondary'} 
                          className="ms-2"
                        >
                          {selectedReceipt.Status}
                        </Badge>
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Receipt Content - PDF or Image */}
              {receiptFileType === 'pdf' ? (
                <div style={{ height: '60vh', width: '100%' }}>
                  <iframe
                    src={receiptImageUrl}
                    title="Receipt PDF"
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px' 
                    }}
                  />
                </div>
              ) : (
                <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                  <img 
                    src={receiptImageUrl} 
                    alt="Receipt" 
                    style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="alert alert-danger">
              Could not load receipt {receiptFileType === 'pdf' ? 'document' : 'image'}. The file may have been deleted.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {receiptImageUrl && (
            <Button 
              variant="outline-primary" 
              onClick={() => {
                const link = document.createElement('a');
                link.href = receiptImageUrl;
                link.download = `receipt_${selectedReceipt?.ScanId || 'file'}.${receiptFileType === 'pdf' ? 'pdf' : 'jpg'}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              <FaDownload className="me-1" /> Download
            </Button>
          )}
          <Button variant="secondary" onClick={handleCloseReceiptImageModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentsTab;
